import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  CircularProgress,
  Divider,
  Grid,
  InputAdornment,
} from '@material-ui/core';
import { Button, Card, Icons, TextField, Typography } from '@nutrien/cxp-components';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { Subscription } from 'rxjs';

import { useMst } from '../../mobx-models/Root';
import { Discipline, InspectionTaskListType } from '../../models/models';
import { InspectionList as InspectionListType } from '../../rxdb/inspectionList/queryBuilder';
import { useInspectionList } from '../../rxdb/inspectionList/useInspectionList';
import { TaskList } from '../../rxdb/taskList/queryBuilder';
import { useTaskList } from '../../rxdb/taskList/useTaskList';
import { ViewInspectorDocument } from '../../rxdb/viewInspector/queryBuilder';
import { useViewInspector } from '../../rxdb/viewInspector/useViewInspector';
import useSelectedWorkPackage from '../../rxdb/workPackages/useSelectedWorkPackage';
import { sortHelper } from '../../utilities';
import { IMAGE_PATH, SORT_BY_ASC, SORT_BY_DESC } from '../../utilities/constants';
import {
  FieldInspectorTab,
  InspectionListTab,
  InspectionStatus,
  LeadInspectorTab,
  TaskStatus,
} from '../../utilities/enums';
import {
  createObjectForBulkReAssignAndSign,
  getValueFromLocalStorage,
  setValueinLocalStorage,
  statusCheck,
} from '../../utilities/utilityFunctions';
import Approval from '../Approval';
import InspectionTab, { TabPanel } from '../InspectionListTabs/InspectionListTabs';
import InspectorInfo from '../InspectorInfo';
import OnGoing from '../OnGoing';
import UnAssigned from '../UnAssigned';
import ViewInspectors from '../ViewInspectors';
import WorkPackageFiles from '../WorkPackageFiles';
import useStyles from './InspectionList.styles';
dayjs.extend(utc);

interface Props {
  setTabValue?: (val: number) => void;
  setFilterOneStyleAdhoc?: (val: boolean) => void;
  setFilterTwoStyleAdhoc?: (val: boolean) => void;
  setILSearchText?: (val: string) => void;
  setCheckInspectionList?: (val: boolean) => void;
}

const InspectionList: React.FC<Props> = (
  {
    setTabValue,
    setFilterOneStyleAdhoc,
    setFilterTwoStyleAdhoc,
    setILSearchText,
    setCheckInspectionList,
  }: Props,
  ref,
) => {
  const { HandleGesture, SiteDiscipline, configCatFlag, CurrentUserSelected } = useMst();
  const workPackageId = CurrentUserSelected.workPackageId;
  const { selectedWorkPackage } = useSelectedWorkPackage(String(workPackageId));
  const isElectrical = SiteDiscipline.disciplineId === Discipline.Electrical;
  const portrait = window.matchMedia('(orientation: portrait)').matches;
  const classes = useStyles({ isElectrical, portrait });
  const inspectionTabValue = Number(
    getValueFromLocalStorage(
      CurrentUserSelected.isAdhoc ? 'adhocInspectionTabValue' : 'inspectionTabValue',
    ),
  );
  const [searchBarOpen, setSearchBarOpen] = useState<boolean>(false);
  const [inProgress, setInProgress] = useState<boolean>(true);
  const [assigned, setAssigned] = useState<boolean>(true);
  const [pendingApproval, setPendingApproval] = useState<boolean>(true);
  const [stamping, setStamping] = useState<boolean>(true);
  const [filterOneStyle, setFilterOneStyle] = useState<boolean>(false);
  const [initialLoader, setInitialLoader] = useState<boolean>(true);
  const [filterTwoStyle, setFilterTwoStyle] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>('');
  const [inspectionListData, setInspectionListData] = useState<InspectionTaskListType[]>([]);
  const [openWorkPackageFilesDialog, setOpenWorkPackageFilesDialog] = useState<boolean>(false);
  const [openViewInspectors, setOpenViewInspectors] = useState<boolean>(false);
  const [tabValue, setValue] = useState<number>(
    HandleGesture.comingFromWorkPackage ? 2 : inspectionTabValue,
  );
  const [allInspectionCheck, setAllInspectionCheck] = useState<boolean>(false);
  const [assignedToMe, setAssignedToMe] = useState<boolean>(false);
  const [inspectionId, setInspectionId] = useState<string>('');
  const [inspectionName, setInspectionName] = useState<string>('');
  const [needAttention, setNeedAttention] = useState<string>('');
  const [inspectionLevel, setInspectionLevel] = useState<string>('');
  const [openFIViewInspectors, setOpenFIViewInspectors] = useState<boolean>(false);
  const [openAddFieldInspector, setOpenAddFieldInspector] = useState<boolean>(false);
  const { inspectionListSort } = sortHelper();
  const { inspectionListCollection, getInspectionTaskList, inspectionTaskListInitialized } =
    useInspectionList();
  const [inspectorData, setInspectorData] = useState<ViewInspectorDocument[]>([]);
  const [taskListData, setTaskListData] = useState<TaskList[]>([]);
  const [countOfInspectionOnTab, setCountOfInspectionOnTab] = useState<{
    unAssigned: number;
    onGoing: number;
    approval: number;
  }>({
    unAssigned: 0,
    onGoing: 0,
    approval: 0,
  });
  const [countOfApprovalInspections, setCountOfApprovalInspections] = useState<{
    assignedToFi: number;
    notAssignedToFi: number;
  }>({ assignedToFi: 0, notAssignedToFi: 0 });
  const [countOfOnGoingInspections, setCountOfOnGoingInspections] = useState<{
    assignedToFi: number;
    notAssignedToFi: number;
  }>({ assignedToFi: 0, notAssignedToFi: 0 });
  const [approvalList, setApprovalList] = useState<InspectionTaskListType[]>([]);
  const [onGoingList, setOnGoingList] = useState<InspectionTaskListType[]>([]);
  const [expanded, setExpanded] = useState<string>('');
  const [timeOutId, setTimeoutId] = useState<NodeJS.Timeout | null | number>(-1);
  const currentInspectorId = CurrentUserSelected?.currentUser?.inspectorId || -1;
  const currentClerkId = CurrentUserSelected?.currentUser?.clerkId || -1;
  const [onHold, setOnHold] = useState<boolean>(false);
  const srcEmptyBox = `${IMAGE_PATH}/emptyBox.svg`;
  const { viewInspectorInitialized, getInspectorOnWorkPackage, viewInspectorCollection } =
    useViewInspector();
  const { taskListInitialized, getTaskList, taskListCollection } = useTaskList();
  const [isGettingData, setIsGettingData] = useState<boolean>(true);
  const [isViewInspector, setIsViewInspectors] = useState<boolean>(false);
  const isAdhoc = CurrentUserSelected.isAdhoc;
  const isAssignToLead = !(isAdhoc || !selectedWorkPackage?.isLeadInspector);
  const [selectedTab, setSelectedTab] = useState<string>(
    isAssignToLead ? InspectionListTab.UN_ASSIGNED : InspectionListTab.ON_GOING,
  );
  const [sortedOngoingInspectionList, setSortedOnGoingInspectionList] = useState<
    InspectionTaskListType[]
  >([]);
  const [unAssignedInspectionList, setUnAssignedInspectionList] = useState<
    InspectionTaskListType[]
  >([]);
  const [sortedApprovalInspectionList, setSortedApprovalInspectionList] = useState<
    InspectionTaskListType[]
  >([]);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    setSelectedTab(isAssignToLead ? InspectionListTab.UN_ASSIGNED : InspectionListTab.ON_GOING);
  }, [isAssignToLead]);

  useEffect(() => {
    setTimeout(() => {
      setInitialLoader(false);
    }, 1000);
  }, [initialLoader]);

  useEffect(() => {
    if (tabValue || tabValue === 0) {
      if (setTabValue) {
        setTabValue(tabValue);
      }
    }
  }, [tabValue]);

  useEffect(() => {
    if (setILSearchText) setILSearchText(searchText);
  }, [searchText]);

  useEffect(() => {
    if (setFilterOneStyleAdhoc) setFilterOneStyleAdhoc(filterOneStyle);
  }, [filterOneStyle]);

  useEffect(() => {
    if (setFilterTwoStyleAdhoc) setFilterTwoStyleAdhoc(filterTwoStyle);
  }, [filterTwoStyle]);

  const multiSelectStatus = (inspection: InspectionTaskListType) => {
    if (HandleGesture.gestureForReAssign) {
      return ![InspectionStatus.IN_PROGRESS, InspectionStatus.ASSIGNED].includes(
        inspection.assetInspectionStatusId,
      );
    }
    return false;
  };

  const isOngoingInspectionHidden = (inspection: InspectionTaskListType) => {
    return (
      (!assigned &&
        (inspection.assetInspectionStatusId === InspectionStatus.ASSIGNED ||
          inspection.assetInspectionStatusId === InspectionStatus.COMPLETED)) ||
      (!inProgress &&
        (inspection.assetInspectionStatusId === InspectionStatus.IN_PROGRESS ||
          inspection.assetInspectionStatusId === InspectionStatus.COMPLETED)) ||
      (assignedToMe &&
        !inspection?.inspectorNameList?.find(
          inspector =>
            inspector.inspectorId === currentInspectorId || inspector.clerkId === currentClerkId,
        )) ||
      (!isAssignToLead &&
        !allInspectionCheck &&
        !isAdhoc &&
        !inspection?.inspectorNameList?.find(
          inspector =>
            inspector.inspectorId === currentInspectorId || inspector.clerkId === currentClerkId,
        )) ||
      multiSelectStatus(inspection)
    );
  };

  const isApprovalInspectionHidden = (inspection: InspectionTaskListType) => {
    return (
      (!pendingApproval &&
        [InspectionStatus.LI_PENDING_APPROVAL, InspectionStatus.SC_PENDING_APPROVAL].includes(
          inspection.assetInspectionStatusId,
        )) ||
      (!stamping &&
        [InspectionStatus.FI_SIGN_PENDING, InspectionStatus.LI_SIGN_PENDING].includes(
          inspection.assetInspectionStatusId,
        )) ||
      (assignedToMe &&
        !inspection.inspectorNameList?.find(
          inspector =>
            inspector.inspectorId === currentInspectorId || inspector.clerkId === currentClerkId,
        )) ||
      (!isAssignToLead &&
        !allInspectionCheck &&
        !isAdhoc &&
        !inspection.inspectorNameList?.find(
          inspector =>
            inspector.inspectorId === currentInspectorId || inspector.clerkId === currentClerkId,
        ))
    );
  };

  const showSearchResult = (inspection: InspectionTaskListType) => {
    if (inspection.assetName?.toLowerCase().includes(searchText?.toLowerCase())) return true;
    else if (inspection.inspectionId.includes(searchText?.toLowerCase())) return true;
    else {
      const names = inspection?.inspectorNameList;
      for (const index in names) {
        if (names[Number(index)].name?.toLowerCase().includes(searchText?.toLowerCase()))
          return true;
      }
    }
    return false;
  };

  const onGoingFilterCheck = (inspection: InspectionTaskListType) =>
    statusCheck(inspection.assetInspectionStatusId, 2) &&
    !isOngoingInspectionHidden(inspection) &&
    showSearchResult(inspection);

  const approvalFilterCheck = (inspection: InspectionTaskListType) =>
    statusCheck(inspection.assetInspectionStatusId, 3) &&
    !isApprovalInspectionHidden(inspection) &&
    showSearchResult(inspection);

  const unAssignFilterCheck = (inspection: InspectionTaskListType) =>
    inspection.assetInspectionStatusId === InspectionStatus.UPCOMING;

  const handleInspectionListSort = (
    inspection1: InspectionTaskListType,
    inspection2: InspectionTaskListType,
  ) =>
    inspectionListSort(
      inspection1,
      inspection2,
      inspectionId,
      inspectionName,
      needAttention,
      inspectionLevel,
    );

  const fetchLatestData = (
    prevData: InspectionTaskListType[],
    newData: InspectionTaskListType[],
    filterFunction: (inspection: InspectionTaskListType) => boolean,
  ) => {
    const oldIds = prevData.map(inspection => inspection.inspectionId);
    const newIds = newData.map(inspection => inspection.inspectionId);
    const data = inspectionListData
      .filter(
        inspection =>
          oldIds.includes(inspection.inspectionId) || newIds.includes(inspection.inspectionId),
      )
      .filter(filterFunction)
      .sort(handleInspectionListSort);
    return data;
  };

  const getPaginatedData = (filterFunction: (inspection: InspectionTaskListType) => boolean) => {
    const filteredData = inspectionListData.filter(filterFunction).sort(handleInspectionListSort);

    if (searchText) {
      return filteredData;
    }

    return filteredData.slice((currentPage - 1) * 10, currentPage * 10);
  };

  const handlePaginatedData = () => {
    if (inspectionListData.length) {
      if (
        (isAdhoc && tabValue === FieldInspectorTab.ONGOING) ||
        (!isAdhoc && isAssignToLead && tabValue === LeadInspectorTab.ONGOING) ||
        (!isAdhoc && !isAssignToLead && tabValue === FieldInspectorTab.ONGOING)
      ) {
        // ongoing tab inspectionListData
        const newData = getPaginatedData(onGoingFilterCheck);
        setSortedOnGoingInspectionList(pre => fetchLatestData(pre, newData, onGoingFilterCheck));
      } else if (
        (isAdhoc && tabValue === FieldInspectorTab.APPROVAL) ||
        (!isAdhoc && isAssignToLead && tabValue === LeadInspectorTab.APPROVAL) ||
        (!isAdhoc && !isAssignToLead && tabValue === FieldInspectorTab.APPROVAL)
      ) {
        // approval tab inspectionListData
        const newData = getPaginatedData(approvalFilterCheck);
        setSortedApprovalInspectionList(pre => fetchLatestData(pre, newData, approvalFilterCheck));
      } else {
        // un assigned tab inspectionListData
        const newData = getPaginatedData(unAssignFilterCheck);
        setUnAssignedInspectionList(pre => fetchLatestData(pre, newData, unAssignFilterCheck));
      }
    }
  };

  useEffect(() => {
    handlePaginatedData();
  }, [inspectionListData, currentPage]);

  useEffect(() => {
    // Should reset all tabs inspectionListData and currentPage while switching tabs
    setCurrentPage(1);
    setUnAssignedInspectionList(() => []);
    setSortedOnGoingInspectionList(() => []);
    setSortedApprovalInspectionList(() => []);
    handlePaginatedData();
  }, [
    tabValue,
    searchText,
    assigned,
    inProgress,
    pendingApproval,
    stamping,
    assignedToMe,
    isAssignToLead,
    allInspectionCheck,
    isAdhoc,
    //sort
    inspectionId,
    inspectionName,
    needAttention,
    inspectionLevel,
  ]);

  const setNotificationClickedTab = () => {
    const notificationInspectionListData = getValueFromLocalStorage('inspectionListData');
    const inspectionID = getValueFromLocalStorage('inspectionID');
    if (!notificationInspectionListData || !inspectionID || !HandleGesture.isNotificationClicked) {
      return;
    }

    const [inspection] = JSON.parse(notificationInspectionListData)?.filter(
      (item: { inspectionId: string }) => item.inspectionId === inspectionID,
    );

    if (inspection) {
      if (statusCheck(inspection.assetInspectionStatusId, 2)) {
        setSelectedTab(InspectionListTab.ON_GOING);
        setValue(0);
      } else if (statusCheck(inspection.assetInspectionStatusId, 3)) {
        setSelectedTab(InspectionListTab.APPROVAL);
        setValue(1);
      } else {
        setSelectedTab(InspectionListTab.UN_ASSIGNED);
        setValue(2);
      }
    }

    HandleGesture.setIsNotificationClicked(false);
  };

  useEffect(() => {
    setNotificationClickedTab();
  }, [HandleGesture.isNotificationClicked, initialLoader]);

  const handleFilterOne = async () => {
    if (tabValue === (!isAssignToLead ? FieldInspectorTab.ONGOING : LeadInspectorTab.ONGOING)) {
      if (assigned) {
        setAssigned(false);
        setFilterOneStyle(true);
        setFilterTwoStyle(false);
        setInProgress(true);
      }
      if (inProgress && !assigned) {
        setAssigned(true);
        setInProgress(true);
        setFilterOneStyle(false);
        setFilterTwoStyle(false);
      }
    } else {
      if (stamping) {
        setStamping(false);
        setFilterOneStyle(true);
        setFilterTwoStyle(false);
        setPendingApproval(true);
      }
      if (pendingApproval && !stamping) {
        setStamping(true);
        setPendingApproval(true);
        setFilterOneStyle(false);
        setFilterTwoStyle(false);
      }
    }
  };

  const handleFilterTwo = async () => {
    if (tabValue === (!isAssignToLead ? FieldInspectorTab.ONGOING : LeadInspectorTab.ONGOING)) {
      if (inProgress) {
        setInProgress(false);
        setFilterTwoStyle(true);
        setFilterOneStyle(false);
        setAssigned(true);
      }
      if (!inProgress && assigned) {
        setAssigned(true);
        setInProgress(true);
        setFilterOneStyle(false);
        setFilterTwoStyle(false);
      }
    } else {
      if (pendingApproval) {
        setPendingApproval(false);
        setFilterTwoStyle(true);
        setFilterOneStyle(false);
        setStamping(true);
      }
      if (!pendingApproval && stamping) {
        setStamping(true);
        setPendingApproval(true);
        setFilterOneStyle(false);
        setFilterTwoStyle(false);
      }
    }
  };

  const handleInspectionIdClick = () => {
    setInspectionName('');
    setNeedAttention('');
    setInspectionLevel('');
    if (inspectionId === SORT_BY_DESC) setInspectionId('');
    else if (inspectionId === '') setInspectionId(SORT_BY_ASC);
    else setInspectionId(SORT_BY_DESC);
  };

  const handleInspectionNameClick = () => {
    setInspectionId('');
    setNeedAttention('');
    setInspectionLevel('');
    if (inspectionName === SORT_BY_DESC) setInspectionName('');
    else if (inspectionName === '') setInspectionName(SORT_BY_ASC);
    else setInspectionName(SORT_BY_DESC);
  };

  const handleNeedAttentionClick = () => {
    setInspectionName('');
    setInspectionId('');
    setInspectionLevel('');
    if (needAttention === SORT_BY_DESC) setNeedAttention('');
    else if (needAttention === '') setNeedAttention(SORT_BY_ASC);
    else setNeedAttention(SORT_BY_DESC);
  };

  const handleInspectionLevelClick = () => {
    setInspectionName('');
    setInspectionId('');
    setInspectionLevel('');
    if (inspectionLevel === SORT_BY_DESC) setInspectionLevel('');
    else if (inspectionLevel === '') setInspectionLevel(SORT_BY_ASC);
    else setInspectionLevel(SORT_BY_DESC);
  };

  const resetSortBy = () => {
    setInspectionName('');
    setNeedAttention('');
    setInspectionLevel('');
    setInspectionId('');
  };

  const resetFilters = () => {
    setAssigned(true);
    setInProgress(true);
    setPendingApproval(true);
    setStamping(true);
    setFilterOneStyle(false);
    setFilterTwoStyle(false);
    setAssignedToMe(false);
    setAllInspectionCheck(false);
    setExpanded('');
  };

  const handleSearch = (event: React.FocusEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setSearchText(value);
  };

  const openWorkPackageFiles = async () => {
    setOpenWorkPackageFilesDialog(true);
  };

  const getInspectionListDataFromRxdb = async () => {
    await getInspectionTaskList(CurrentUserSelected.workPackageId).then(data => {
      const previousData = localStorage.getItem('inspectionListData');
      if (JSON.stringify(data) !== previousData) {
        localStorage.setItem('inspectionListData', JSON.stringify(data));
        setInspectionListData(data);
        setCountOfInspectionOnTab({
          unAssigned: data?.filter(inspection => statusCheck(inspection.assetInspectionStatusId, 1))
            ?.length,
          onGoing: data?.filter(inspection => statusCheck(inspection.assetInspectionStatusId, 2))
            ?.length,
          approval: data?.filter(inspection => statusCheck(inspection.assetInspectionStatusId, 3))
            ?.length,
        });
        setApprovalList([
          ...approvalList,
          ...data?.filter(inspection => statusCheck(inspection.assetInspectionStatusId, 3)),
        ]);
        setOnGoingList([
          ...onGoingList,
          ...data?.filter(inspection => statusCheck(inspection.assetInspectionStatusId, 2)),
        ]);
        if (
          HandleGesture.runOnDeletion &&
          (HandleGesture.runOnInspectionSub || HandleGesture.runOnTaskSub)
        ) {
          if (JSON.parse(previousData || '[]').length > data?.length) {
            HandleGesture.setRunOnDeletion(false);
          } else {
            HandleGesture.setRunOnInspectionSubscription(false);
            HandleGesture.setRunOnTaskSubscription(false);
          }
        } else {
          setIsGettingData(false);
          HandleGesture.setIsCompleted(false);
          HandleGesture.setRunOnInspectionSubscription(false);
          HandleGesture.setRunOnTaskSubscription(false);
          HandleGesture.setRunOnDeletion(false);
        }
      }
    });
  };

  const getTaskListDataFromRxdb = async () => {
    const inspectionListDocument = await getTaskList();
    if (inspectionListDocument) {
      setTaskListData(JSON.parse(JSON.stringify(inspectionListDocument)));
    }
  };

  const getViewInspectorDataFromRxdb = async () => {
    const viewInspectorDocumentData = await getInspectorOnWorkPackage(workPackageId);
    if (viewInspectorDocumentData) {
      setInspectorData(viewInspectorDocumentData);
    }
  };

  const onClickDown = (inspId: string, statusId: number) => {
    if (!HandleGesture.bulkUpload) {
      const tid: NodeJS.Timeout | null = setTimeout(() => {
        HandleGesture.insertIntoInspectionId(Number(inspId));
        HandleGesture.insertIntoAccordion(inspId);
        const singleInspection = inspectionListData?.find(
          inspection => inspection.inspectionId === inspId,
        );
        singleInspection?.taskList
          ?.filter(task => task.fkInspectionTaskStatusCategory !== TaskStatus.COMPLETED)
          ?.forEach(task => {
            HandleGesture.insertIntoTaskId(task.pkAssetInspectionTask);
            HandleGesture.setTasksForReAssignAndSign(
              createObjectForBulkReAssignAndSign(task, statusId),
              true,
            );
          });
        HandleGesture.setGestureForReAssign(true);
        setOnHold(true);
        setExpanded(inspId);
      }, 1000);
      setTimeoutId(tid);
    }
  };

  const handleAccordianChange = (panel: string) => {
    if (expanded === panel) {
      setExpanded('false');
    } else {
      setExpanded(panel);
    }
  };

  const onClickUp = (inspId: string) => {
    clearTimeout(timeOutId as NodeJS.Timeout);
    if (onHold) {
      setOnHold(false);
    } else {
      if (HandleGesture.bulkUpload && !onHold) {
        if (HandleGesture.handleAccordion.includes(inspId))
          HandleGesture.removeFromAccordion(inspId);
        else HandleGesture.insertIntoAccordion(inspId);
      }
      handleAccordianChange(inspId);
    }
  };

  const onRelease = (e: React.TouchEvent<HTMLDivElement>, inspId: string) => {
    onClickUp(inspId);
    e.preventDefault();
  };

  const renderAccordian = (
    id: string,
    statusId: number,
    label: React.ReactElement,
    content: (false | JSX.Element)[] | undefined,
  ) => {
    return (
      <Accordion
        elevation={0}
        className={classes.accordion}
        expanded={
          !HandleGesture.bulkUpload ? expanded === id : HandleGesture.handleAccordion.includes(id)
        }
        onMouseDown={() => onClickDown(id, statusId)}
        onMouseUp={() => onClickUp(id)}
        onTouchStart={() => onClickDown(id, statusId)}
        onTouchEnd={e => onRelease(e, id)}
        TransitionProps={{ unmountOnExit: true, mountOnEnter: true }}
      >
        <AccordionSummary
          expandIcon={<Icons.ChevronDownFeather color="primary" />}
          aria-controls="panel1a-content"
          id="panel1a-header"
        >
          <Typography className="colorGroup font-18 font-weight-600" id="accordionlabel">
            {label}
          </Typography>
        </AccordionSummary>
        <AccordionDetails
          onMouseDown={e => {
            e.stopPropagation();
          }}
          onMouseUp={e => {
            e.stopPropagation();
          }}
          onTouchStart={e => {
            e.stopPropagation();
          }}
          onTouchEnd={e => {
            e.stopPropagation();
          }}
        >
          <Divider orientation="horizontal" className={classes.accordianDivider} />
          {content}
        </AccordionDetails>
      </Accordion>
    );
  };

  const checkForReassign = (assetInspectionStatusId: number) => {
    return (
      ([
        InspectionStatus.LI_PENDING_APPROVAL,
        InspectionStatus.SC_PENDING_APPROVAL,
        InspectionStatus.FI_SIGN_PENDING,
        InspectionStatus.LI_SIGN_PENDING,
      ].includes(assetInspectionStatusId) &&
        tabValue === (!isAssignToLead ? FieldInspectorTab.APPROVAL : LeadInspectorTab.APPROVAL)) ||
      ((assetInspectionStatusId === InspectionStatus.IN_PROGRESS ||
        assetInspectionStatusId === InspectionStatus.ASSIGNED) &&
        tabValue === (!isAssignToLead ? FieldInspectorTab.ONGOING : LeadInspectorTab.ONGOING))
    );
  };

  const checkLISignAndStamp = (statusId: number, leadInspectorId = 0) => {
    return statusId === InspectionStatus.LI_SIGN_PENDING && leadInspectorId === currentInspectorId;
  };

  const checkUser = (fieldInspectorId?: number, clerkId?: number) => {
    if (CurrentUserSelected.isAdhoc) return true;
    const isLeadInspector = isAssignToLead;
    if (!assignedToMe && allInspectionCheck) return true;
    if (assignedToMe && isLeadInspector) {
      if (fieldInspectorId ? fieldInspectorId !== currentInspectorId : clerkId !== currentClerkId) {
        return false;
      }
      return true;
    }
    if (!allInspectionCheck && !isLeadInspector) {
      if (fieldInspectorId ? fieldInspectorId === currentInspectorId : clerkId === currentClerkId) {
        return true;
      }
      return false;
    }
    return true;
  };

  const checkSearchText = (name: string, id: string) => {
    if (name?.toLowerCase().includes(searchText?.toLowerCase())) return true;
    else if (id?.toLowerCase().includes(searchText?.toLowerCase())) return true;
    else {
      const singleInspection = inspectionListData?.find(
        inspection => inspection.inspectionId === id,
      );
      const tasks = singleInspection?.taskList || [];
      for (const i in tasks) {
        if (tasks[Number(i)]?.name?.toLowerCase().includes(searchText?.toLowerCase())) return true;
      }
    }
    return false;
  };

  const checkQuickFilter = (statusId: number) => {
    if (!inProgress) {
      return statusId !== InspectionStatus.IN_PROGRESS;
    }
    if (!assigned) {
      return statusId !== InspectionStatus.ASSIGNED;
    }
    return true;
  };

  const checkUnAssignedStatusInInspection = (inspection: InspectionListType) => {
    return (
      inspection.assetInspectionStatusId === InspectionStatus.UPCOMING &&
      checkSearchText(inspection.assetName, inspection.inspectionId)
    );
  };

  useEffect(() => {
    if (HandleGesture.gestureForReAssign) {
      inspectionListData?.forEach(inspection => {
        if (
          !checkSearchText(inspection.assetName, inspection.inspectionId) ||
          !checkQuickFilter(inspection?.assetInspectionStatusId)
        ) {
          HandleGesture.removeFromInspectionId(Number(inspection.inspectionId));
          inspection?.taskList?.forEach((task: TaskList) => {
            HandleGesture.removeFromTaskId(Number(task.pkAssetInspectionTask));
            HandleGesture.setTasksForReAssignAndSign(
              createObjectForBulkReAssignAndSign(task, inspection?.assetInspectionStatusId),
              false,
            );
          });
        }
      });
    }
  }, [assigned, inProgress]);

  useEffect(() => {
    if (HandleGesture.gestureForUnAssigned) {
      inspectionListData.forEach(inspection => {
        if (!checkUnAssignedStatusInInspection(inspection))
          HandleGesture.removeFromInspectionId(Number(inspection.inspectionId));
      });
    } else if (HandleGesture.gestureForSignAndStamp || HandleGesture.gestureForReAssign) {
      inspectionListData.forEach(inspection => {
        if (
          !checkSearchText(inspection.assetName, inspection.inspectionId) &&
          checkQuickFilter(inspection?.assetInspectionStatusId)
        ) {
          HandleGesture.removeFromInspectionId(Number(inspection.inspectionId));
          inspection?.taskList?.forEach((task: TaskList) => {
            HandleGesture.removeFromTaskId(Number(task.pkAssetInspectionTask));
            HandleGesture.setTasksForReAssignAndSign(
              createObjectForBulkReAssignAndSign(task, inspection?.assetInspectionStatusId),
              false,
            );
          });
        }
      });
    }
  }, [searchText]);

  const handleSignAndStamp = () => {
    if (HandleGesture.gestureForSignAndStamp) {
      let isLIstampingRequired = false;
      inspectionListData.forEach(inspection => {
        if (
          inspection.assetInspectionStatusId === InspectionStatus.FI_SIGN_PENDING &&
          checkSearchText(inspection.assetName, inspection.inspectionId)
        ) {
          const data2 = inspection?.taskList?.filter(
            task =>
              (currentInspectorId === task.fkInspectorField || currentClerkId === task.clerkId) &&
              task.fkInspectionTaskStatusCategory === TaskStatus.FI_SIGN_PENDING &&
              checkUser(task.fkInspectorField, task.clerkId),
          );

          if (data2 && data2?.length > 0) {
            const data = inspection?.taskList?.filter(
              task =>
                currentInspectorId === task.fkInspectorField &&
                task.fkInspectionTaskStatusCategory === TaskStatus.FI_SIGN_PENDING,
            );

            if (data && data.length) {
              HandleGesture.insertIntoInspectionId(Number(inspection.inspectionId));
              data.forEach(task => {
                HandleGesture.insertIntoTaskId(Number(task.pkAssetInspectionTask));
                HandleGesture.setTasksForReAssignAndSign(
                  createObjectForBulkReAssignAndSign(task, inspection?.assetInspectionStatusId),
                  true,
                );
              });
            }
          }
        } else if (
          checkLISignAndStamp(
            inspection.assetInspectionStatusId,
            inspection?.taskList ? inspection?.taskList[0]?.fkInspectorLead : 0,
          ) &&
          checkSearchText(inspection.assetName, inspection.inspectionId)
        ) {
          HandleGesture.insertIntoInspectionId(Number(inspection.inspectionId));
          isLIstampingRequired = isLIstampingRequired || inspection.isLIstampingRequired;
          inspection?.taskList?.forEach(task => {
            if (task.fkInspectionTaskStatusCategory === TaskStatus.LI_SIGN_PENDING) {
              HandleGesture.insertIntoTaskId(Number(task.pkAssetInspectionTask));
              HandleGesture.setTasksForReAssignAndSign(
                createObjectForBulkReAssignAndSign(task, inspection?.assetInspectionStatusId),
                true,
              );
            }
          });
        }
      });

      HandleGesture.setIsLIstampingRequired(isLIstampingRequired || false);
    }
  };

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = e.target;
    if (checked) {
      if (HandleGesture.gestureForUnAssigned) {
        inspectionListData.forEach(inspection => {
          if (checkUnAssignedStatusInInspection(inspection))
            HandleGesture.insertIntoInspectionId(Number(inspection.inspectionId));
        });
      }
      if (HandleGesture.gestureForSignAndStamp) {
        handleSignAndStamp();
      }
      if (HandleGesture.gestureForReAssign) {
        inspectionListData.forEach(inspection => {
          if (
            checkForReassign(inspection?.assetInspectionStatusId) &&
            checkSearchText(inspection.assetName, inspection.inspectionId) &&
            checkQuickFilter(inspection?.assetInspectionStatusId)
          ) {
            const data2 = inspection?.taskList?.filter(
              task =>
                task.fkInspectionTaskStatusCategory !== TaskStatus.COMPLETED &&
                checkUser(task?.fkInspectorField, task?.clerkId),
            );
            if (data2 && data2?.length > 0) {
              const data = inspection?.taskList?.filter(
                task => task.fkInspectionTaskStatusCategory !== TaskStatus.COMPLETED,
              );
              HandleGesture.insertIntoInspectionId(Number(inspection.inspectionId));
              data?.forEach((task: TaskList) => {
                HandleGesture.insertIntoTaskId(Number(task.pkAssetInspectionTask));
                HandleGesture.setTasksForReAssignAndSign(
                  createObjectForBulkReAssignAndSign(task, inspection?.assetInspectionStatusId),
                  true,
                );
              });
            }
          }
        });
      }
      HandleGesture.changeSelectAll(true);
    } else {
      HandleGesture.resetInspectionId();
      HandleGesture.resetTaskId();
      HandleGesture.resetTaskForReAssignAndSign();
      HandleGesture.changeSelectAll(false);
    }
  };

  const checkTabValue = () => {
    if (!HandleGesture.bulkUpload) {
      return (
        tabValue !== (!isAssignToLead ? FieldInspectorTab.UNASSIGN : LeadInspectorTab.UNASSIGN)
      );
    }
    return (
      HandleGesture.gestureForReAssign &&
      tabValue !== (!isAssignToLead ? FieldInspectorTab.APPROVAL : LeadInspectorTab.APPROVAL)
    );
  };

  const endAndornmentSearch = () => {
    if (searchText.length !== 0) {
      return (
        <InputAdornment position="end">
          <Icons.XFeather
            color="primary"
            className={classes.searchTextField}
            onClick={() => setSearchText('')}
          />
        </InputAdornment>
      );
    }
  };

  const checkInspectionList = useMemo(() => {
    if (isAdhoc) return false;
    const condition1 = onGoingList?.length === 0 && isAssignToLead;

    const condition2 =
      countOfOnGoingInspections.assignedToFi === 0 &&
      ((!allInspectionCheck && !isAssignToLead) ||
        (assignedToMe && onGoingList?.length !== 0 && isAssignToLead));

    const condition3 =
      countOfOnGoingInspections.notAssignedToFi === 0 &&
      countOfOnGoingInspections.assignedToFi === 0 &&
      !isAssignToLead &&
      allInspectionCheck;

    const condition4 = approvalList?.length === 0 && isAssignToLead;

    const condition5 =
      countOfApprovalInspections.assignedToFi === 0 &&
      ((!allInspectionCheck && !isAssignToLead) ||
        (assignedToMe && approvalList?.length !== 0 && isAssignToLead));

    const condition6 =
      countOfApprovalInspections.notAssignedToFi === 0 &&
      countOfApprovalInspections.assignedToFi === 0 &&
      !isAssignToLead &&
      allInspectionCheck;

    const condition7 =
      inspectionListData.filter(
        inspection => inspection.assetInspectionStatusId === InspectionStatus.UPCOMING,
      ).length === 0;

    if (selectedTab.includes('On Going')) {
      return condition1 || condition2 || condition3;
    }
    if (selectedTab.includes('Approval')) {
      return condition4 || condition5 || condition6;
    }
    return condition7;
  }, [
    selectedTab,
    isAssignToLead,
    onGoingList,
    countOfOnGoingInspections,
    allInspectionCheck,
    assignedToMe,
    approvalList,
    countOfApprovalInspections,
    inspectionListData,
  ]);

  useEffect(() => {
    if (inspectionListData.length > 0 && approvalList.length > 0) {
      setCountOfApprovalInspections({ assignedToFi: 0, notAssignedToFi: 0 });
      approvalList?.map(inspection => {
        if (
          inspection?.taskList?.find(
            task =>
              task.fkInspectorField === Number(getValueFromLocalStorage('inspectorId')) ||
              task.clerkId === Number(getValueFromLocalStorage('clerkId')),
          )
        ) {
          setCountOfApprovalInspections(count => ({
            ...count,
            assignedToFi: count.assignedToFi + 1,
          }));
        } else {
          setCountOfApprovalInspections(count => ({
            ...count,
            notAssignedToFi: count.notAssignedToFi + 1,
          }));
        }
      });
    }
  }, [inspectionListData, approvalList]);

  useEffect(() => {
    if (inspectionListData.length > 0 && onGoingList.length > 0) {
      setCountOfOnGoingInspections({ assignedToFi: 0, notAssignedToFi: 0 });
      onGoingList?.map(inspection => {
        if (
          inspection.taskList?.find(
            task =>
              task.fkInspectorField === Number(getValueFromLocalStorage('inspectorId')) ||
              task.clerkId === Number(getValueFromLocalStorage('clerkId')),
          )
        ) {
          setCountOfOnGoingInspections(count => ({
            ...count,
            assignedToFi: count.assignedToFi + 1,
          }));
        } else {
          setCountOfOnGoingInspections(count => ({
            ...count,
            notAssignedToFi: count.notAssignedToFi + 1,
          }));
        }
      });
    }
  }, [inspectionListData, onGoingList]);

  useEffect(() => {
    let inspSub: Subscription;
    let queryObj = {};
    if (inspectionTaskListInitialized) {
      getInspectionListDataFromRxdb();
      if (inspectionListCollection) {
        if (isAdhoc) {
          queryObj = {
            selector: {
              isAdhoc: { $eq: isAdhoc },
            },
          };
        } else {
          queryObj = {
            selector: {
              workPackageId: { $eq: workPackageId },
            },
          };
        }
        inspSub = inspectionListCollection.find(queryObj).$.subscribe(() => {
          if (!HandleGesture.runOnTaskSub || HandleGesture.runOnDeletion) {
            getInspectionListDataFromRxdb();
          }
        });
      }
    }
    return () => {
      localStorage.removeItem('inspectionListData');
      localStorage.removeItem('assetCategoryId');
      if (!HandleGesture.comingFromWorkPackage) {
        HandleGesture.resetEverything();
      } else {
        HandleGesture.setcomingFromWorkPackage(false);
      }
      if (inspSub && inspSub.unsubscribe) inspSub.unsubscribe();
    };
  }, [inspectionTaskListInitialized]);

  useEffect(() => {
    let taskSub: Subscription;
    if (taskListInitialized) {
      getTaskListDataFromRxdb();
      if (taskListCollection) {
        const queryObj = {
          selector: isAdhoc
            ? {
                $and: [
                  { site: SiteDiscipline.siteId },
                  { discipline: SiteDiscipline.disciplineId },
                ],
              }
            : {
                fkSchedule: { $eq: workPackageId },
              },
        };
        taskSub = taskListCollection.find(queryObj).$.subscribe(() => {
          if (!HandleGesture.runOnInspectionSub) {
            getInspectionListDataFromRxdb();
          }
          getTaskListDataFromRxdb();
        });
      }
    }
    return () => {
      if (taskSub && taskSub.unsubscribe) taskSub.unsubscribe();
    };
  }, [taskListInitialized]);

  useEffect(() => {
    let viewInspectorSub: Subscription;
    if (viewInspectorInitialized) {
      getViewInspectorDataFromRxdb();
      if (viewInspectorCollection) {
        const queryObj = {
          selector: {
            workPackageId: { $eq: workPackageId },
          },
        };
        viewInspectorSub = viewInspectorCollection.find(queryObj).$.subscribe(() => {
          getViewInspectorDataFromRxdb();
        });
      }
    }
    return () => {
      if (viewInspectorSub && viewInspectorSub.unsubscribe) viewInspectorSub.unsubscribe();
    };
  }, [viewInspectorInitialized]);

  useEffect(() => {
    setSearchText('');
    setSearchBarOpen(false);
  }, [
    window.matchMedia('(orientation: portrait)').matches,
    window.matchMedia('(orientation: landscape)').matches,
  ]);

  useEffect(() => {
    if (HandleGesture.gestureForUnAssigned) {
      const filteredInspectionArray = inspectionListData.filter(checkUnAssignedStatusInInspection);
      if (
        filteredInspectionArray.length &&
        filteredInspectionArray.every((inspection: InspectionListType) =>
          HandleGesture.inspectionIds.includes(Number(inspection.inspectionId)),
        )
      )
        HandleGesture.changeSelectAll(true);
      else HandleGesture.changeSelectAll(false);
    }
    if (HandleGesture.gestureForSignAndStamp) {
      let actualLength = 0;
      inspectionListData
        .filter(inspection => checkSearchText(inspection.assetName, inspection.inspectionId))
        .forEach(inspection => {
          if (inspection.assetInspectionStatusId === InspectionStatus.FI_SIGN_PENDING) {
            const data2 = inspection?.taskList?.filter(
              task =>
                (currentInspectorId === task.fkInspectorField || currentClerkId === task.clerkId) &&
                task.fkInspectionTaskStatusCategory === TaskStatus.FI_SIGN_PENDING &&
                checkUser(task?.fkInspectorField, task?.clerkId),
            );
            if (data2 && data2?.length > 0) {
              actualLength += data2?.length;
            }
          } else if (
            checkLISignAndStamp(
              inspection.assetInspectionStatusId,
              inspection?.taskList[0]?.fkInspectorLead,
            )
          ) {
            actualLength += 1;
          }
        });

      let isLIstampingRequired = false;
      HandleGesture.inspectionIds.forEach((id: number) => {
        const matchedRecord = inspectionListData.filter(
          inspection =>
            Number(inspection.inspectionId) === id &&
            checkLISignAndStamp(
              inspection.assetInspectionStatusId,
              inspection?.taskList ? inspection?.taskList[0]?.fkInspectorLead : 0,
            ),
        );
        if (matchedRecord?.length) {
          isLIstampingRequired = isLIstampingRequired || matchedRecord[0].isLIstampingRequired;
        }
      });
      HandleGesture.setIsLIstampingRequired(isLIstampingRequired || false);

      if (HandleGesture.taskIds.length === actualLength) HandleGesture.changeSelectAll(true);
      else HandleGesture.changeSelectAll(false);
    }
    if (HandleGesture.gestureForReAssign) {
      let actualLength = 0;
      inspectionListData
        .filter(
          inspection =>
            checkSearchText(inspection.assetName, inspection.inspectionId) &&
            checkQuickFilter(inspection?.assetInspectionStatusId),
        )
        .forEach(inspection => {
          if (checkForReassign(inspection?.assetInspectionStatusId)) {
            const data2 = inspection?.taskList?.filter(
              task =>
                task.fkInspectionTaskStatusCategory !== TaskStatus.COMPLETED &&
                checkUser(task.fkInspectorField, task.clerkId),
            );
            if (data2 && data2?.length > 0) {
              const data = inspection?.taskList?.filter(
                task => task.fkInspectionTaskStatusCategory !== TaskStatus.COMPLETED,
              );
              actualLength += data?.length;
            }
          }
        });
      if (HandleGesture.taskIds.length === actualLength && actualLength !== 0)
        HandleGesture.changeSelectAll(true);
      else HandleGesture.changeSelectAll(false);
    }
  }, [
    HandleGesture,
    HandleGesture.gestureForUnAssigned,
    HandleGesture.inspectionIds.length,
    HandleGesture.taskIds.length,
    countOfInspectionOnTab.unAssigned,
    checkForReassign,
    inspectionListData,
    searchText,
    inProgress,
    assigned,
  ]);

  useEffect(() => {
    if (taskListData.length > 0) {
      setValueinLocalStorage('leadInspectorId', String(taskListData[0]?.fkInspectorLead));
    }
  }, [taskListData]);

  useEffect(() => {
    if (setCheckInspectionList) setCheckInspectionList(checkInspectionList);
  }, [checkInspectionList]);

  const openInspectorView = () => {
    setIsViewInspectors(true);
    if (!isAssignToLead) {
      setOpenViewInspectors(true);
    } else {
      setOpenFIViewInspectors(true);
      if (inspectorData.length === 0) {
        setIsViewInspectors(false);
        setOpenAddFieldInspector(true);
      }
    }
  };

  useImperativeHandle(ref, () => ({
    handleSearchfromWP(event: React.FocusEvent<HTMLInputElement>) {
      handleSearch(event);
    },
    handleFilterOnefromWP() {
      handleFilterOne();
    },
    handleFilterTwofromWP() {
      handleFilterTwo();
    },
    openWorkPackageFilesfromWP() {
      openWorkPackageFiles();
    },
    endAndornmentSearchfromWP() {
      endAndornmentSearch();
    },
  }));

  useEffect(() => {
    // for handling adhoc bulk sign and stamp and reAssign
    if (
      HandleGesture.bulkUpload &&
      ((isAssignToLead && tabValue !== LeadInspectorTab.UNASSIGN) ||
        (!isAssignToLead && tabValue !== FieldInspectorTab.UNASSIGN))
    ) {
      if (HandleGesture?.tasksforReAssignAndSign?.length) {
        let isReAssign = false;
        let isSignAndStamp = false;

        HandleGesture.tasksforReAssignAndSign.forEach(task => {
          if (
            (task.fieldInspectorId === currentInspectorId &&
              task.taskStatusId === TaskStatus.FI_SIGN_PENDING &&
              task.inspectionStatusId === InspectionStatus.FI_SIGN_PENDING) ||
            (task.leadInspectorId === currentInspectorId &&
              task.taskStatusId === TaskStatus.LI_SIGN_PENDING &&
              task.inspectionStatusId === InspectionStatus.LI_SIGN_PENDING)
          ) {
            isSignAndStamp = true;
          } else {
            isReAssign = true;
          }
        });
        if (isReAssign) {
          HandleGesture.setGestureForSignAndStamp(false);
        } else if (isSignAndStamp) {
          HandleGesture.setGestureForSignAndStamp(true);
        }
      } else {
        HandleGesture.setGestureForSignAndStamp(false);
      }
      HandleGesture.setGestureForReAssign(true);
    }
  }, [
    HandleGesture.tasksforReAssignAndSign.length,
    HandleGesture.bulkUpload,
    tabValue,
    isAssignToLead,
  ]);

  const handleScroll = e => {
    const target = e.target;
    const scrollTop = target.scrollTop;
    const scrollHeight = target.scrollHeight;
    const clientHeight = target.clientHeight;

    if (scrollHeight - scrollTop - clientHeight <= 10) {
      setCurrentPage(prevPage => prevPage + 1);
    }
  };

  return (
    <>
      <WorkPackageFiles
        open={openWorkPackageFilesDialog}
        onClose={() => setOpenWorkPackageFilesDialog(false)}
        workPackageId={String(workPackageId)}
      />
      {!isAdhoc && (
        <Card elevation={1} className={classes.card}>
          <Grid container spacing={2}>
            <Grid item container xs={12} alignItems="center">
              {searchBarOpen && portrait ? (
                <Grid xs={12} item className={classes.searchBarOuterGrid}>
                  <TextField
                    inputProps={{ 'data-testid': 'inspection-search-input' }}
                    data-testid="inspection-search"
                    hidden={checkInspectionList}
                    fullWidth
                    noMinHeight
                    autoFocus
                    focused
                    variant="outlined"
                    size="small"
                    startAdornment={
                      <InputAdornment position="start">
                        <Icons.SearchFeather hidden={checkInspectionList} color="primary" />
                      </InputAdornment>
                    }
                    className={classes.testSearchBoxPotrait}
                    placeholder="Search"
                    onChange={handleSearch}
                  />
                  <Grid hidden={checkInspectionList} item className={classes.xFeatherGrid}>
                    <Icons.XFeather
                      color="primary"
                      onClick={() => {
                        setSearchText('');
                        setSearchBarOpen(false);
                      }}
                    />
                  </Grid>
                  <Grid hidden={checkInspectionList} item className={classes.searchBarDividerGrid}>
                    <Divider orientation="vertical" className={classes.divider} />
                  </Grid>
                  {!CurrentUserSelected.isAdhoc && (
                    <Grid
                      data-testid="openWorkpageDialog"
                      item
                      onClick={openWorkPackageFiles}
                      className={classes.downloadIcon}
                    >
                      <Icons.DownloadFeather color="primary" />
                    </Grid>
                  )}
                </Grid>
              ) : (
                <>
                  <Grid
                    className={classes.gridheading}
                    item
                    xs={
                      isElectrical
                        ? 3
                        : [FieldInspectorTab.APPROVAL, LeadInspectorTab.ONGOING].includes(
                            tabValue,
                          ) && !portrait
                        ? 3
                        : 4
                    }
                  >
                    <Typography id="inspectionListHeading">
                      {isElectrical
                        ? `PM List (${
                            inspectionListData?.filter(
                              e => e.assetInspectionStatusId !== InspectionStatus.COMPLETED,
                            )?.length
                          })`
                        : `Inspection List (${
                            inspectionListData?.filter(
                              e => e.assetInspectionStatusId !== InspectionStatus.COMPLETED,
                            )?.length
                          })`}
                    </Typography>
                    <Typography
                      display="block"
                      color="primary"
                      id="viewInspector"
                      data-testid="add-view-inspector"
                      onClick={openInspectorView}
                    >
                      {inspectorData.length === 0 && isAssignToLead
                        ? 'Add Field Inspector'
                        : 'View Inspectors'}
                    </Typography>
                  </Grid>

                  <Grid
                    item
                    xs={
                      isElectrical
                        ? 9
                        : [FieldInspectorTab.APPROVAL, LeadInspectorTab.ONGOING].includes(
                            tabValue,
                          ) && !portrait
                        ? 9
                        : 8
                    }
                    container
                    justifyContent="flex-end"
                  >
                    {checkTabValue() && !HandleGesture.gestureForSignAndStamp && (
                      <>
                        <Grid hidden={checkInspectionList} item className={classes.quickFilterGrid}>
                          <Typography variant="body1" className={classes.quickFilterText}>
                            Quick Filter
                          </Typography>
                        </Grid>
                        <Grid
                          hidden={checkInspectionList}
                          item
                          className={`${classes.statusGrid} ${classes.filterbutton}`}
                        >
                          <Button
                            data-testid="ins-list-inProgress"
                            id="inProgress"
                            color="secondary"
                            variant={filterOneStyle ? 'contained' : undefined}
                            noMinHeight
                            onClick={handleFilterOne}
                            data-cy="in progress"
                            className={`${
                              filterOneStyle ? 'buttonWhenContained' : 'buttonWhenNotContained'
                            }`}
                          >
                            <Typography variant="body1">
                              {tabValue ===
                              (!isAssignToLead
                                ? FieldInspectorTab.ONGOING
                                : LeadInspectorTab.ONGOING)
                                ? 'In Progress'
                                : 'Pending Approval'}
                            </Typography>
                          </Button>
                        </Grid>

                        <Grid
                          hidden={checkInspectionList}
                          item
                          className={`${classes.stampingGrid} ${classes.filterbutton}`}
                        >
                          <Button
                            data-testid="ins-list-assigned"
                            id="assigned"
                            color="secondary"
                            variant={filterTwoStyle ? 'contained' : undefined}
                            noMinHeight
                            onClick={handleFilterTwo}
                            data-cy="assigned"
                            className={`${
                              filterTwoStyle ? 'buttonWhenContained' : 'buttonWhenNotContained'
                            }`}
                          >
                            <Typography variant="body1">
                              {tabValue ===
                              (!isAssignToLead
                                ? FieldInspectorTab.ONGOING
                                : LeadInspectorTab.ONGOING)
                                ? 'Assigned'
                                : 'Stamping'}
                            </Typography>
                          </Button>
                        </Grid>
                        <Grid hidden={checkInspectionList} item className={classes.diverderGrid}>
                          <Divider orientation="vertical" className={classes.divider} />
                        </Grid>
                      </>
                    )}
                    {portrait &&
                    ![FieldInspectorTab.UNASSIGN, LeadInspectorTab.APPROVAL].includes(tabValue) ? (
                      <Grid item className={checkInspectionList ? 'iconHidden' : 'iconVisible'}>
                        <Icons.SearchFeather
                          color="primary"
                          onClick={() => setSearchBarOpen(true)}
                        />
                      </Grid>
                    ) : (
                      <Grid hidden={checkInspectionList} item className={classes.searchField}>
                        <Grid className="searchTextbox">
                          <TextField
                            inputProps={{ 'data-testid': 'inspection-search-input' }}
                            data-testid="inspection-search"
                            hidden={checkInspectionList}
                            noMinHeight
                            variant="outlined"
                            size="small"
                            type="text"
                            focused
                            startAdornment={
                              <InputAdornment position="start">
                                <Icons.SearchFeather hidden={checkInspectionList} color="primary" />
                              </InputAdornment>
                            }
                            endAdornment={endAndornmentSearch()}
                            placeholder="Search"
                            value={searchText}
                            onChange={handleSearch}
                          />
                        </Grid>
                      </Grid>
                    )}

                    <Grid
                      hidden={checkInspectionList}
                      item
                      className={classes.searchBarDividerGrid}
                    >
                      <Divider orientation="vertical" className={classes.divider} />
                    </Grid>
                    <Grid
                      data-testid="openWorkpageDialog"
                      item
                      onClick={openWorkPackageFiles}
                      className={classes.downloadIcon}
                    >
                      <Icons.DownloadFeather color="primary" />
                    </Grid>
                  </Grid>
                </>
              )}
            </Grid>
          </Grid>
        </Card>
      )}

      <Grid className={classes.inspectionTabDiv}>
        <Card elevation={1} id="inspectionTabCard">
          <InspectionTab
            inspectorData={inspectorData}
            handleSelectAll={handleSelectAll}
            tabValue={tabValue}
            setValue={setValue}
            allInspectionCheck={allInspectionCheck}
            setAllInspectionCheck={setAllInspectionCheck}
            assignedToMe={assignedToMe}
            setAssignedToMe={setAssignedToMe}
            inspectionId={inspectionId}
            inspectionName={inspectionName}
            needAttention={needAttention}
            inspectionLevel={inspectionLevel}
            handleInspectionIdClick={handleInspectionIdClick}
            handleInspectionNameClick={handleInspectionNameClick}
            handleNeedAttentionClick={handleNeedAttentionClick}
            handleInspectionLevelClick={handleInspectionLevelClick}
            resetSortBy={resetSortBy}
            resetFilters={resetFilters}
            countOfInspectionOnTab={countOfInspectionOnTab}
            taskList={taskListData}
            setSelectedTab={setSelectedTab}
            checkInspectionList={checkInspectionList}
            inspectionList={inspectionListData}
            setInitialLoader={setInitialLoader}
            isAssignToLead={isAssignToLead}
          />
        </Card>
      </Grid>
      <Grid
        className={
          configCatFlag.getBlueBannerDisplay === true || configCatFlag.getRedBannerDisplay === true
            ? classes.root1
            : classes.root
        }
        onScroll={handleScroll}
      >
        {(inspectionListData?.length == 0 && isGettingData) || initialLoader ? (
          <Grid item xs={12} id="circularProgressGrid">
            <CircularProgress color="primary" />
          </Grid>
        ) : (
          <>
            <TabPanel
              value={tabValue}
              index={!isAssignToLead ? FieldInspectorTab.ONGOING : LeadInspectorTab.ONGOING}
            >
              <Grid className={classes.inspectionListDataDiv}>
                <Card
                  elevation={1}
                  id="inspectionListDataCard"
                  data-testid="inspection-list-datacard"
                >
                  {sortedOngoingInspectionList.map(inspection => (
                    <OnGoing
                      key={inspection.inspectionId}
                      inspection={inspection}
                      inspectorData={inspectorData}
                      renderAccordian={renderAccordian}
                      isAssignToLead={isAssignToLead}
                    />
                  ))}
                  {isAdhoc ? (
                    <>
                      {onGoingList?.length === 0 && (
                        <Grid className={classes.messageGrid}>
                          <img src={srcEmptyBox} className={classes.emptyBox} />
                          <Typography
                            variant="h6"
                            color="textSecondary"
                            className={classes.divText}
                          >
                            It's empty. There are no On-going Inspections assigned to you.
                          </Typography>
                        </Grid>
                      )}
                    </>
                  ) : (
                    <>
                      {onGoingList?.length === 0 && isAssignToLead && (
                        <Grid className={classes.messageGrid}>
                          <img src={srcEmptyBox} className={classes.emptyBox} />
                          <Typography
                            variant="h6"
                            color="textSecondary"
                            className={classes.divText}
                          >
                            It's empty. There are no On-going Inspections in the Work Package.
                          </Typography>
                        </Grid>
                      )}
                      {countOfOnGoingInspections.assignedToFi === 0 &&
                        ((!allInspectionCheck && !isAssignToLead) ||
                          (assignedToMe && onGoingList?.length !== 0 && isAssignToLead)) && (
                          <Grid className={classes.messageGrid}>
                            <img src={srcEmptyBox} className={classes.emptyBox} />
                            <Grid className={classes.divText}>
                              <Typography variant="h6" color="textSecondary">
                                It's empty. There are no onGoing inspections assigned to you in the
                                workpackage.
                              </Typography>
                              <Typography variant="h6" color="textSecondary">
                                Please check {isAdhoc ? 'Approval' : 'Un-Assigned'} Tab for
                                available inspections.
                              </Typography>
                            </Grid>
                          </Grid>
                        )}
                      {countOfOnGoingInspections.notAssignedToFi === 0 &&
                        countOfOnGoingInspections.assignedToFi === 0 &&
                        !isAssignToLead &&
                        allInspectionCheck && (
                          <Grid className={classes.messageGrid}>
                            <img src={srcEmptyBox} className={classes.emptyBox} />
                            <Grid className={classes.divText}>
                              <Typography variant="h6" color="textSecondary">
                                It's empty. There are no onGoing inspections in the workpackage.
                              </Typography>
                              <Typography variant="h6" color="textSecondary">
                                Please check {isAdhoc ? 'Approval' : 'Un-Assigned'} Tab for
                                available inspections.
                              </Typography>
                            </Grid>
                          </Grid>
                        )}
                    </>
                  )}
                </Card>
              </Grid>
            </TabPanel>
            <TabPanel
              value={tabValue}
              index={!isAssignToLead ? FieldInspectorTab.APPROVAL : LeadInspectorTab.APPROVAL}
            >
              <Card
                elevation={1}
                id="inspectionApprovalDataCard"
                data-testid="inspection-approval-data-card"
              >
                {sortedApprovalInspectionList.map(inspection => (
                  <Approval
                    key={inspection.inspectionId}
                    inspectionData={inspection}
                    inspectorData={inspectorData}
                    renderAccordian={renderAccordian}
                    isAssignToLead={isAssignToLead}
                  />
                ))}
                {isAdhoc ? (
                  <>
                    {approvalList?.length === 0 && (
                      <Grid className={classes.messageGrid}>
                        <img src={srcEmptyBox} className={classes.emptyBox} />
                        <Typography variant="h6" color="textSecondary" className={classes.divText}>
                          It's empty. There are no Inspections available for approval.
                        </Typography>
                      </Grid>
                    )}
                  </>
                ) : (
                  <>
                    {approvalList?.length === 0 && isAssignToLead && (
                      <Grid className={classes.messageGrid}>
                        <img src={srcEmptyBox} className={classes.emptyBox} />
                        <Typography variant="h6" color="textSecondary" className={classes.divText}>
                          It's empty. There are no Inspections available for approval in the Work
                          package.
                        </Typography>
                      </Grid>
                    )}

                    {countOfApprovalInspections.assignedToFi === 0 &&
                      ((!allInspectionCheck && !isAssignToLead) ||
                        (assignedToMe && approvalList?.length !== 0 && isAssignToLead)) && (
                        <Grid className={classes.messageGrid}>
                          <img src={srcEmptyBox} className={classes.emptyBox} />
                          <Typography
                            variant="h6"
                            color="textSecondary"
                            className={classes.divText}
                          >
                            It's empty. There are no Approval Inspections assigned to you in the
                            Work Package.
                          </Typography>
                        </Grid>
                      )}
                    {countOfApprovalInspections.notAssignedToFi === 0 &&
                      countOfApprovalInspections.assignedToFi === 0 &&
                      !isAssignToLead &&
                      allInspectionCheck && (
                        <Grid className={classes.messageGrid}>
                          <img src={srcEmptyBox} className={classes.emptyBox} />
                          <Typography
                            variant="h6"
                            color="textSecondary"
                            className={classes.divText}
                          >
                            It's empty. There are no Inspections submitted for approval in the Work
                            package.
                          </Typography>
                        </Grid>
                      )}
                  </>
                )}
              </Card>
            </TabPanel>
            {!isAdhoc && (
              <TabPanel
                data-testid="unassigned-tab"
                value={tabValue}
                index={!isAssignToLead ? FieldInspectorTab.UNASSIGN : LeadInspectorTab.UNASSIGN}
              >
                <UnAssigned
                  inspectorData={inspectorData.filter(
                    inspector =>
                      inspector.fieldInspectorId !==
                      Number(getValueFromLocalStorage('inspectorId')),
                  )}
                  inspectionListData={unAssignedInspectionList}
                  searchText={searchText}
                  sortInspectionId={inspectionId}
                  inspectionName={inspectionName}
                  needAttention={needAttention}
                  inspectionLevel={inspectionLevel}
                  isAssignToLead={isAssignToLead}
                />
              </TabPanel>
            )}
          </>
        )}
      </Grid>

      {openViewInspectors && (
        <InspectorInfo
          open={openViewInspectors}
          onClose={() => setOpenViewInspectors(false)}
          workPackageId={workPackageId}
        />
      )}

      {openFIViewInspectors && (
        <ViewInspectors
          openViewInspectors={openFIViewInspectors}
          setOpenViewInspectors={setOpenFIViewInspectors}
          openAddFieldInspector={openAddFieldInspector}
          setOpenAddFieldInspector={setOpenAddFieldInspector}
          workPackageId={workPackageId}
          isViewInspectors={isViewInspector}
        />
      )}
    </>
  );
};
export default observer(InspectionList, { forwardRef: true });
